import { useTranslation } from 'react-i18next';
import { AccountBalance, GetColorStatus, SubPartnerAccount, useDateLocale, withStyles } from 'vopay';
import { TransactionStatus } from 'UI';
import usePartnerAccountStatusLabel from '@/components/Entities/hooks/usePartnerAccountStatusLabel';
import { formatAccountDate, getStatusDateDisplay } from '../accountStatusDate';
import derivePartnerAccountStatus from '../partnerAccountStatus';
import styles from './AccountStatus.module.scss';

const cn = withStyles(styles);

type AccountStatusProps = {
  /**
   * An account, or a partner account, whose status reads by the same rule. A partner account also
   * carries `DateActivated`, which dates an Active one when its status history recorded nothing.
   */
  account: Pick<AccountBalance | SubPartnerAccount, 'AccountStatus' | 'IsActive' | 'AccountStatusDate'> &
    Partial<Pick<SubPartnerAccount, 'DateActivated'>>;
  /**
   * How an Active account's date reads: the list says "Since <date>" like every other settled
   * status, the account card says "Activated on <date>".
   */
  activeDateCopy?: 'since' | 'activatedOn';
  /**
   * `card` right-aligns the pair and puts it on the account card's two-line rhythm, so the badge
   * lines up with the account name beside it and the date with the currency below that.
   */
  layout?: 'inline' | 'card';
};

/**
 * The status badge plus, underneath, the date a settled status was entered or the days spent in the
 * current onboarding stage (spec 5.3 / 5.5). Shared by the list's Status column and the account
 * card so the two never disagree on what a status is called or when it started.
 */
export default function AccountStatus({ account, activeDateCopy = 'since', layout = 'inline' }: AccountStatusProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountStatus' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();
  const dateLocale = useDateLocale();

  const rawStatus = derivePartnerAccountStatus(account);

  // An Active partner account dates from its activation, falling back to its status date when none
  // was recorded (the same rule as the partner list's Status column and card).
  const statusDateSource =
    rawStatus === 'Active' ? (account.DateActivated ?? account.AccountStatusDate) : account.AccountStatusDate;

  const statusDate = rawStatus ? getStatusDateDisplay(rawStatus, statusDateSource) : null;

  const formatDate = (date: Date) => formatAccountDate(date, dateLocale);

  return (
    <div className={cn('statusCell', layout)} data-testid="accountStatus">
      {/* The badge sets its own height, so the card's 32px line box has to come from around it. */}
      <div className={cn('badgeLine')}>
        {rawStatus === 'Blocked' ? (
          <TransactionStatus label={resolveStatusLabel('Blocked')} status="blocked" />
        ) : rawStatus === 'Active' ? (
          <TransactionStatus label={resolveStatusLabel('Active')} status="positive" />
        ) : (
          !!rawStatus && <TransactionStatus label={resolveStatusLabel(rawStatus)} status={GetColorStatus(rawStatus)} />
        )}
      </div>

      {statusDate?.kind === 'since' && (
        <span className={cn('statusMeta')}>
          {rawStatus === 'Active' && activeDateCopy === 'activatedOn'
            ? t('activatedOn', { defaultValue: 'Activated on {{date}}', date: formatDate(statusDate.date) })
            : t('statusSince', { defaultValue: 'Since {{date}}', date: formatDate(statusDate.date) })}
        </span>
      )}

      {statusDate?.kind === 'daysInStage' && (
        <span className={cn('statusMeta')}>
          {t('daysInStage', { defaultValue: '{{count}} days in this stage', count: statusDate.numDays })}
        </span>
      )}
    </div>
  );
}
